<template>
  <div class="data-edit data-edit--gradient">
    <form-factory
      formclass="bank-details__content"
      :record="account"
      :schema="bankAccountFields"
      :layout="layout"
      :hasError="error"
      :submitText="getUiString(['claim_details', 'details', 'bank_details', 'save_button'])"
      :cancelText="getUiString(['claim_details', 'details', 'bank_details', 'cancel_button'])"
       @fieldinput="checkInput($event.field, $event.value)"
      @submit="saveSettings"
      @reset="resetForm"
      v-model="account.CountryCode"
    >
      <h2 class="title--l">{{ getUiString(['claim_details', 'details', 'bank_details', 'title']) }}</h2>
      <template v-slot:bottomtext>
        <div class="form__row">
          <div class="cell">
            <a class="link" @click.prevent="switchAccountType">{{ altLinkText }}</a>
          </div>
        </div>
      </template>
    </form-factory>
    <lennoc-spinner v-show="busy" isFullscreen="false" />
  </div>
</template>

<script>
import { splitIntoColumns } from '@/utils/formComposition.js'
import { getFormFields } from '@/utils/formFields'
import { mapGetters } from 'vuex'

export default {
  name: 'EditBankDetails',
  props: {
    initialFields: {
      type: Array,
      required: false,
      default () {
        return []
      }
    }
  },
  data: function () {
    return {
      account: {},
      error: false,
      busy: false,
      requiredBankAccountFields: [],
      currentCountryCode: '',
      usePaypalAccount: false
    }
  },
  computed: {
    ...mapGetters({
      getUiString: 'uistrings/getUiString',
      storedBankAccount: 'claim/bankAccount',
      claimId: 'claim/claimId',
      emptyAccount: 'claim/getEmptyBankAccount',
      storedPaypalAccount: 'claim/paypalAddress'
    }),
    requiredBankFields () {
      if (this.usePaypalAccount) {
        return ['PayPalEMailAccount']
      }
      return this.requiredBankAccountFields
    },
    layout () {
      return splitIntoColumns(this.requiredBankFields)
    },
    bankAccountFields () {
      const fields = getFormFields(this.requiredBankFields)
      const countryCode = this.storedBankAccount.CountryCode
      if (fields.CountryCode) { fields.CountryCode.default = countryCode }
      return fields
    },
    altLinkText () {
      if (this.usePaypalAccount) {
        return this.getUiString(['claim_details', 'details', 'bank_details', 'change_to_bankaccount'])
      }
      return this.getUiString(['claim_details', 'details', 'bank_details', 'change_to_paypal'])
    },
    altDescription () {
      if (this.usePaypalAccount) {
        return this.getUiString(['claim_details', 'details', 'bank_details', 'paypal_text'])
      }
      return this.getUiString(['claim_details', 'details', 'bank_details', 'text'])
    }
  },
  beforeMount () {
    this.account = { ...this.emptyAccount }
    this.usePaypalAccount = this.initialFields[0] === 'PayPalEMailAccount'
    this.initializeAccount()
  },
  mounted () {
  },
  methods: {
    switchAccountType () {
      this.busy = true
      const usePaypal = !this.usePaypalAccount
      const paymentMethod = usePaypal ? 'PayPal' : 'Bank'
      const accountData = { claimId: this.claimId, paymentMethod: paymentMethod }
      this.$store.dispatch('claim/updatePaymentMethod', accountData).then(() => {
        this.usePaypalAccount = usePaypal
        this.$store.dispatch('claim/refreshClaim').then(() => {
          this.initializeAccount()
        }).finally(() => {
          this.busy = false
        })
      })
    },
    initializeAccount () {
      if (this.usePaypalAccount) {
        this.$set(this.account, 'PayPalEMailAccount', this.storedPaypalAccount)
      } else {
        const countryCode = this.account.CountryCode || this.storedBankAccount.CountryCode || 'NL'
        this.getRequiredFields(countryCode)
        Object.keys(this.storedBankAccount).forEach(p => {
          this.$set(this.account, p, this.storedBankAccount[p])
        })
      }
    },
    resetForm () {
      this.account = { ...this.emptyAccount }
      this.$emit('cancel')
    },
    checkInput (field, value) {
      if (field === 'CountryCode' && value && this.currentCountryCode !== value) {
        this.account.CountryCode = value
        this.getRequiredFields(value)
        Object.keys(this.storedBankAccount).forEach(p => {
          this.$set(this.account, p, this.storedBankAccount[p])
        })
        this.bankAccountFields.CountryCode.default = value
      }
    },
    saveSettings (record) {
      this.busy = true
      let storeAction
      const accountData = { Id: this.claimId }
      if (this.usePaypalAccount) {
        accountData.PayPalEMailAccount = record.PayPalEMailAccount
        storeAction = 'claim/updatePayPalEmail'
      } else {
        this.requiredBankFields.forEach(f => {
          if (record[f]) {
            accountData[f] = record[f]
          } else {
            this.error = true
          }
        })
        storeAction = 'claim/updateBankAccountDetails'
      }
      this.$store
        .dispatch(storeAction, accountData)
        .then(() => {
          this.$store.dispatch('claim/refreshClaim').then(() => {
            this.busy = false
            this.$emit('ready')
          })
        })
    },
    getRequiredFields (countryCode) {
      this.busy = true
      this.currentCountryCode = countryCode
      this.$store
        .dispatch('claim/getRequiredBankAccountFields', countryCode)
        .then(result => {
          this.requiredBankAccountFields = [
            'CountryCode',
            ...result.BankAccountField
          ]
        })
        .finally(() => {
          this.busy = false
        })
    }
  }
}
</script>
